<template>
    <div class="rtp-phase-tracker">
        <!-- Header -->
        <div class="tracker-header">
            <h4>
                <i class="bi bi-activity"></i>
                Return-to-Play Protocol
            </h4>
            <div v-if="rtpData && rtpData.maturity_at_injury" class="maturity-badge"
                :class="'maturity-' + rtpData.maturity_at_injury?.toLowerCase().replace('-', '')">
                <i class="bi bi-person-badge"></i>
                {{ rtpData.maturity_at_injury }}
                <span v-if="rtpData.maturity_at_injury === 'Circa-PHV'" class="maturity-warning">
                    <i class="bi bi-exclamation-triangle"></i> 48hr between phases
                </span>
            </div>
        </div>

        <!-- Maturity Warning Banner -->
        <div v-if="maturityOutdated" class="maturity-warning-banner">
            <i class="bi bi-exclamation-triangle-fill"></i>
            <span>Maturity data is {{ maturityDaysOld }} days old (>90 days). Consider re-assessment before RTP
                progression.</span>
        </div>

        <!-- Phase Stepper -->
        <div class="phase-stepper">
            <div v-for="phase in 5" :key="phase" class="phase-step" :class="{
                'completed': currentPhase > phase,
                'active': currentPhase === phase,
                'upcoming': currentPhase < phase,
                'regression': hasRegression(phase)
            }">
                <div class="step-indicator">
                    <div class="step-circle">
                        <i v-if="currentPhase > phase" class="bi bi-check-lg"></i>
                        <i v-else-if="hasRegression(phase)" class="bi bi-exclamation"></i>
                        <span v-else>{{ phase }}</span>
                    </div>
                    <div v-if="phase < 5" class="step-line" :class="{ 'completed': currentPhase > phase }"></div>
                </div>
                <div class="step-content">
                    <span class="step-name">{{ getPhaseInfo(phase).name }}</span>
                    <span class="step-short">P{{ phase }}</span>
                </div>
            </div>
        </div>

        <!-- Current Phase Details -->
        <div v-if="currentPhase" class="current-phase-details">
            <div class="phase-info-card" :style="{ borderColor: getPhaseInfo(currentPhase).color }">
                <div class="phase-header" :style="{ backgroundColor: getPhaseInfo(currentPhase).bgColor }">
                    <div class="phase-title">
                        <span class="phase-badge" :style="{ backgroundColor: getPhaseInfo(currentPhase).color }">
                            Phase {{ currentPhase }}
                        </span>
                        <h5>{{ getPhaseInfo(currentPhase).name }}</h5>
                    </div>
                    <div class="days-counter">
                        <span class="days-number">{{ daysInCurrentPhase }}</span>
                        <span class="days-label">days</span>
                    </div>
                </div>

                <div class="phase-body">
                    <p class="phase-description">{{ getPhaseInfo(currentPhase).description }}</p>

                    <div class="phase-meta">
                        <div class="meta-item" v-if="currentPhaseLead">
                            <i class="bi bi-person"></i>
                            <span class="meta-label">Phase Lead:</span>
                            <span class="meta-value">{{ currentPhaseLead }}</span>
                        </div>
                        <div class="meta-item" v-if="rtpData?.rtp_next_review_date">
                            <i class="bi bi-calendar-check"></i>
                            <span class="meta-label">Next Review:</span>
                            <span class="meta-value" :class="{ 'overdue': isReviewOverdue }">
                                {{ formatDate(rtpData.rtp_next_review_date) }}
                                <span v-if="isReviewOverdue" class="overdue-badge">OVERDUE</span>
                            </span>
                        </div>
                        <div class="meta-item" v-if="rtpData?.rtp_target_date">
                            <i class="bi bi-flag"></i>
                            <span class="meta-label">Target Return:</span>
                            <span class="meta-value">{{ formatDate(rtpData.rtp_target_date) }}</span>
                        </div>
                    </div>

                    <!-- Restrictions -->
                    <div v-if="rtpData?.rtp_restrictions" class="restrictions-section">
                        <h6><i class="bi bi-shield-exclamation"></i> Current Restrictions</h6>
                        <p>{{ rtpData.rtp_restrictions }}</p>
                    </div>

                    <!-- Phase Overview -->
                    <div class="phase-overview-section">
                        <h6><i class="bi bi-info-circle"></i> Phase {{ currentPhase }} Overview</h6>
                        <div class="overview-grid">
                            <div class="overview-item">
                                <span class="overview-label">Minimum Duration:</span>
                                <span class="overview-value">{{ getPhaseInfo(currentPhase).minDuration }}</span>
                            </div>
                            <div class="overview-item">
                                <span class="overview-label">Lead:</span>
                                <span class="overview-value">{{ getPhaseInfo(currentPhase).leadDisplay }}</span>
                            </div>
                            <div class="overview-item full-width">
                                <span class="overview-label">Activities:</span>
                                <span class="overview-value">{{ getPhaseInfo(currentPhase).activities }}</span>
                            </div>
                        </div>
                        <div class="progression-criteria">
                            <span class="criteria-label">Progression Criteria:</span>
                            <ul class="criteria-list">
                                <li v-for="(criterion, index) in getPhaseInfo(currentPhase).progressionCriteria"
                                    :key="index">
                                    {{ criterion }}
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Action Buttons -->
        <div v-if="currentPhase && showActions" class="phase-actions">
            <!-- Progress Button -->
            <button v-if="currentPhase < 5" class="btn btn-success btn-progress" :disabled="!canProgress"
                @click="openProgressModal">
                <i class="bi bi-arrow-right-circle"></i>
                Progress to Phase {{ currentPhase + 1 }}
            </button>

            <!-- Complete Button (Phase 5 only) -->
            <button v-if="currentPhase === 5" class="btn btn-primary btn-complete" :disabled="!canComplete"
                @click="openCompleteModal">
                <i class="bi bi-check-circle"></i>
                Final Clearance
            </button>

            <!-- Regress Button -->
            <button v-if="currentPhase > 1" class="btn btn-outline-danger btn-regress" @click="openRegressModal">
                <i class="bi bi-arrow-left-circle"></i>
                Report Setback
            </button>

            <!-- Edit Details Button -->
            <button class="btn btn-outline-secondary" @click="openEditModal">
                <i class="bi bi-pencil"></i>
                Edit Details
            </button>

            <!-- Permission Warning -->
            <div v-if="!canProgress && currentPhase < 5" class="permission-warning">
                <i class="bi bi-info-circle"></i>
                <span>Required role for progression: {{ getRequiredRolesText() }}</span>
            </div>

            <!-- Time Warning -->
            <div v-if="hoursRemaining > 0" class="time-warning">
                <i class="bi bi-clock"></i>
                <span>{{ hoursRemaining }} hours remaining before progression allowed</span>
            </div>
        </div>

        <!-- Recovery Drills Section -->
        <div v-if="currentPhase && showActions" class="recovery-drills-section">
            <div class="recovery-drills-header">
                <h6>
                    <i class="bi bi-heart-pulse"></i>
                    Recovery Drills — Phase {{ currentPhase }}
                </h6>
                <button v-if="canManageRecoveryDrills" class="btn btn-sm btn-outline-primary"
                    @click="openRecoveryDrillPicker">
                    <i class="bi bi-plus-circle"></i>
                    Add Recovery Drills
                </button>
            </div>

            <div v-if="assignedRecoveryDrills.length === 0" class="recovery-empty-state">
                <i class="bi bi-clipboard-x"></i>
                <span>No recovery drills assigned for Phase {{ currentPhase }}</span>
            </div>

            <div v-else class="recovery-drill-list">
                <div v-for="drill in assignedRecoveryDrills" :key="drill.id" class="recovery-drill-item">
                    <div class="drill-item-image" v-if="drill.imageURL">
                        <img :src="drill.imageURL" :alt="drill.name" />
                    </div>
                    <div class="drill-item-image drill-item-image--placeholder" v-else>
                        <i class="bi bi-collection-play"></i>
                    </div>
                    <div class="drill-item-info">
                        <span class="drill-item-name">{{ drill.name }}</span>
                        <span v-if="drill.category" class="drill-item-category">{{ drill.category }}</span>
                    </div>
                    <button v-if="canManageRecoveryDrills" class="btn btn-sm btn-outline-danger drill-remove-btn"
                        @click="removeRecoveryDrill(drill.id)" title="Remove drill">
                        <i class="bi bi-x"></i>
                    </button>
                </div>
            </div>
        </div>

        <!-- Recovery Drill Picker Modal -->
        <div v-if="showRecoveryDrillPicker" class="recovery-picker-overlay" @click.self="showRecoveryDrillPicker = false">
            <div class="recovery-picker-modal">
                <div class="recovery-picker-header">
                    <h5>
                        <i class="bi bi-heart-pulse"></i>
                        Recovery Library — Phase {{ currentPhase }}
                    </h5>
                    <button class="btn btn-sm btn-secondary" @click="showRecoveryDrillPicker = false">
                        <i class="bi bi-x-lg"></i> Done
                    </button>
                </div>

                <div class="recovery-picker-search">
                    <input v-model="recoveryDrillSearch" type="text" class="form-control"
                        placeholder="Search recovery drills..." />
                </div>

                <div v-if="loadingRecoveryDrills" class="recovery-picker-loading">
                    <i class="bi bi-hourglass-split"></i> Loading drills...
                </div>

                <div v-else-if="filteredRecoveryLibraryDrills.length === 0" class="recovery-picker-empty">
                    <i class="bi bi-search"></i>
                    <span v-if="recoveryDrillSearch">No drills match "{{ recoveryDrillSearch }}"</span>
                    <span v-else>No Recovery drills found in the library. Add drills to the Recovery section of the Training Library first.</span>
                </div>

                <div v-else class="recovery-picker-grid">
                    <div v-for="drill in filteredRecoveryLibraryDrills" :key="drill.id"
                        class="recovery-picker-card"
                        :class="{ 'is-assigned': isRecoveryDrillAssigned(drill.id) }"
                        @click="toggleRecoveryDrill(drill)">
                        <div class="picker-card-image" v-if="drill.imageURL">
                            <img :src="drill.imageURL" :alt="drill.name" />
                        </div>
                        <div class="picker-card-image picker-card-image--placeholder" v-else>
                            <i class="bi bi-collection-play"></i>
                        </div>
                        <div class="picker-card-body">
                            <div class="picker-card-name">{{ drill.name }}</div>
                            <div v-if="drill.category" class="picker-card-category">{{ drill.category }}</div>
                            <div v-if="drill.introText" class="picker-card-intro">{{ drill.introText }}</div>
                        </div>
                        <div class="picker-card-action">
                            <i v-if="isRecoveryDrillAssigned(drill.id)" class="bi bi-check-circle-fill assigned-icon"></i>
                            <i v-else class="bi bi-plus-circle add-icon"></i>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Progress Modal -->
        <b-modal v-model="showProgressModal" title="Progress to Next Phase" size="lg" :hide-footer="true">
            <form @submit.prevent="submitProgress">
                <div class="modal-phase-info">
                    <div class="phase-transition">
                        <span class="from-phase" :style="{ backgroundColor: getPhaseInfo(currentPhase).color }">
                            P{{ currentPhase }}: {{ getPhaseInfo(currentPhase).name }}
                        </span>
                        <i class="bi bi-arrow-right"></i>
                        <span class="to-phase" :style="{ backgroundColor: getPhaseInfo(currentPhase + 1).color }">
                            P{{ currentPhase + 1 }}: {{ getPhaseInfo(currentPhase + 1).name }}
                        </span>
                    </div>
                </div>

                <div class="form-group mb-3">
                    <label class="form-label required">Sign-Off Comments *</label>
                    <textarea v-model="progressForm.sign_off_comments" class="form-control" rows="3"
                        placeholder="Describe the player's progress and readiness for next phase..."
                        required></textarea>
                </div>

                <div class="form-group mb-3">
                    <label class="form-label">New Phase Restrictions</label>
                    <textarea v-model="progressForm.restrictions" class="form-control" rows="2"
                        placeholder="Any restrictions for the new phase..."></textarea>
                </div>

                <div class="form-group mb-3">
                    <label class="form-label">Next Review Date</label>
                    <input type="date" v-model="progressForm.next_review_date" class="form-control" />
                </div>

                <div class="form-group mb-3">
                    <label class="form-label">Phase Lead for Phase {{ currentPhase + 1 }} (optional)</label>
                    <p class="form-text text-muted mb-2">
                        <small>Recommended: {{ getPhaseInfo(currentPhase + 1).leadDisplay }}</small>
                    </p>
                    <select v-model="progressForm.phase_lead_id" class="form-control">
                        <option value="">-- Same as current --</option>
                        <optgroup v-if="recommendedStaff.length > 0" label="Recommended for this phase">
                            <option v-for="staff in recommendedStaff" :key="staff.id" :value="staff.id">
                                {{ staff.name }} ({{ staff.position }})
                            </option>
                        </optgroup>
                        <optgroup v-if="otherStaff.length > 0" label="Other staff">
                            <option v-for="staff in otherStaff" :key="staff.id" :value="staff.id">
                                {{ staff.name }} ({{ staff.position }})
                            </option>
                        </optgroup>
                    </select>
                </div>

                <div class="modal-actions">
                    <button type="button" class="btn btn-secondary" @click="showProgressModal = false">Cancel</button>
                    <button type="submit" class="btn btn-success" :disabled="isSubmitting">
                        <span v-if="isSubmitting"><i class="bi bi-hourglass-split"></i> Processing...</span>
                        <span v-else><i class="bi bi-check-lg"></i> Confirm Progression</span>
                    </button>
                </div>
            </form>
        </b-modal>

        <!-- Regress Modal -->
        <b-modal v-model="showRegressModal" title="Report Setback / Adverse Reaction" size="lg" :hide-footer="true">
            <form @submit.prevent="submitRegress">
                <div class="alert alert-warning">
                    <i class="bi bi-exclamation-triangle"></i>
                    <strong>Recording a setback</strong> - This will move the player back to an earlier phase and log
                    the adverse reaction.
                </div>

                <div class="form-group mb-3">
                    <label class="form-label required">Adverse Reaction / Reason for Setback *</label>
                    <textarea v-model="regressForm.adverse_reaction" class="form-control" rows="3"
                        placeholder="Describe what happened - pain, swelling, loss of function, etc..."
                        required></textarea>
                </div>

                <div class="form-group mb-3">
                    <label class="form-label">Regress to Phase</label>
                    <select v-model="regressForm.new_phase" class="form-control">
                        <option v-for="phase in regressPhaseOptions" :key="phase" :value="phase">
                            Phase {{ phase }}: {{ getPhaseInfo(phase).name }}
                        </option>
                    </select>
                </div>

                <div class="form-group mb-3">
                    <label class="form-label">Updated Restrictions</label>
                    <textarea v-model="regressForm.restrictions" class="form-control" rows="2"
                        placeholder="Any new restrictions..."></textarea>
                </div>

                <div class="form-group mb-3">
                    <label class="form-label">Additional Comments</label>
                    <textarea v-model="regressForm.sign_off_comments" class="form-control" rows="2"
                        placeholder="Additional notes..."></textarea>
                </div>

                <div class="modal-actions">
                    <button type="button" class="btn btn-secondary" @click="showRegressModal = false">Cancel</button>
                    <button type="submit" class="btn btn-danger" :disabled="isSubmitting">
                        <span v-if="isSubmitting"><i class="bi bi-hourglass-split"></i> Processing...</span>
                        <span v-else><i class="bi bi-arrow-left-circle"></i> Confirm Regression</span>
                    </button>
                </div>
            </form>
        </b-modal>

        <!-- Complete Modal -->
        <b-modal v-model="showCompleteModal" title="Final Clearance - Return to Play" size="lg" :hide-footer="true">
            <form @submit.prevent="submitComplete">
                <div class="alert alert-success">
                    <i class="bi bi-check-circle"></i>
                    <strong>Final Clearance</strong> - This will mark the player as fully recovered and cleared for full
                    participation.
                </div>

                <div class="form-group mb-3">
                    <label class="form-label required">Final Clearance Comments *</label>
                    <textarea v-model="completeForm.sign_off_comments" class="form-control" rows="4"
                        placeholder="Document the player's readiness for full return to play, any ongoing monitoring requirements, etc..."
                        required></textarea>
                </div>

                <div class="clearance-checklist">
                    <h6>Clearance Checklist</h6>
                    <div class="checklist-item">
                        <input type="checkbox" v-model="completeForm.checklist_physical" id="check_physical" />
                        <label for="check_physical">Physical tests passed</label>
                    </div>
                    <div class="checklist-item">
                        <input type="checkbox" v-model="completeForm.checklist_training" id="check_training" />
                        <label for="check_training">Full training completed without issues</label>
                    </div>
                    <div class="checklist-item">
                        <input type="checkbox" v-model="completeForm.checklist_confidence" id="check_confidence" />
                        <label for="check_confidence">Player confident and psychologically ready</label>
                    </div>
                </div>

                <div class="modal-actions">
                    <button type="button" class="btn btn-secondary" @click="showCompleteModal = false">Cancel</button>
                    <button type="submit" class="btn btn-primary" :disabled="isSubmitting || !allChecklistComplete">
                        <span v-if="isSubmitting"><i class="bi bi-hourglass-split"></i> Processing...</span>
                        <span v-else><i class="bi bi-check-circle"></i> Grant Final Clearance</span>
                    </button>
                </div>
            </form>
        </b-modal>

        <!-- Edit Details Modal -->
        <b-modal v-model="showEditModal" title="Edit RTP Details" size="md" :hide-footer="true">
            <form @submit.prevent="submitEditDetails">
                <div class="form-group mb-3">
                    <label class="form-label">Current Restrictions</label>
                    <textarea v-model="editForm.restrictions" class="form-control" rows="3"></textarea>
                </div>

                <div class="form-group mb-3">
                    <label class="form-label">Next Review Date</label>
                    <input type="date" v-model="editForm.next_review_date" class="form-control" />
                </div>

                <div class="form-group mb-3">
                    <label class="form-label">Target Return Date</label>
                    <input type="date" v-model="editForm.target_date" class="form-control" />
                </div>

                <!-- Duration Override Section (Admin/Physio only) -->
                <div v-if="canOverrideDuration && hoursRemaining > 0" class="form-group mb-3 duration-override-section">
                    <div class="override-warning alert alert-warning mb-2">
                        <i class="bi bi-exclamation-triangle"></i>
                        <strong>Minimum Duration Active:</strong> {{ hoursRemaining }} hours remaining before
                        progression is normally allowed.
                    </div>
                    <div class="form-check">
                        <input type="checkbox" class="form-check-input" id="overrideDuration"
                            v-model="editForm.override_duration" />
                        <label class="form-check-label" for="overrideDuration">
                            <strong>Override Minimum Duration</strong>
                            <small class="d-block text-muted">Allow immediate progression to next phase (Admin/Physio
                                authorization)</small>
                        </label>
                    </div>
                    <div v-if="editForm.override_duration" class="override-confirmation mt-2">
                        <div class="form-group">
                            <label class="form-label required">Override Reason *</label>
                            <textarea v-model="editForm.override_reason" class="form-control" rows="2"
                                placeholder="Explain why the minimum duration is being overridden..."
                                :required="editForm.override_duration"></textarea>
                        </div>
                    </div>
                </div>

                <div class="modal-actions">
                    <button type="button" class="btn btn-secondary" @click="showEditModal = false">Cancel</button>
                    <button type="submit" class="btn btn-primary"
                        :disabled="isSubmitting || (editForm.override_duration && !editForm.override_reason)">
                        <span v-if="isSubmitting"><i class="bi bi-hourglass-split"></i> Saving...</span>
                        <span v-else><i class="bi bi-check-lg"></i> Save Changes</span>
                    </button>
                </div>
            </form>
        </b-modal>
    </div>
</template>

<script>
import { ReturnToPlayService, RTP_PHASES, canProgressPhase, getRequiredRoles, getMinHoursBetweenPhases } from "@/services/rtp-service.js";
import { Users } from "@/services/user-service.js";

export default {
    name: "RTPPhaseTracker",
    props: {
        injuryId: {
            type: [Number, String],
            required: true
        },
        userData: {
            type: Object,
            required: true
        },
        showActions: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            rtpData: null,
            phaseHistory: [],
            loading: true,
            isSubmitting: false,

            // Modals
            showProgressModal: false,
            showRegressModal: false,
            showCompleteModal: false,
            showEditModal: false,

            // Form data
            progressForm: {
                sign_off_comments: '',
                restrictions: '',
                next_review_date: '',
                phase_lead_id: ''
            },
            regressForm: {
                adverse_reaction: '',
                new_phase: 1,
                restrictions: '',
                sign_off_comments: ''
            },
            completeForm: {
                sign_off_comments: '',
                checklist_physical: false,
                checklist_training: false,
                checklist_confidence: false
            },
            editForm: {
                restrictions: '',
                next_review_date: '',
                target_date: '',
                override_duration: false,
                override_reason: ''
            },

            // Staff list for phase lead selection
            availableStaff: [],

            // Recovery Drills
            assignedRecoveryDrills: [],
            recoveryLibraryDrills: [],
            showRecoveryDrillPicker: false,
            loadingRecoveryDrills: false,
            recoveryDrillSearch: ''
        };
    },
    computed: {
        currentPhase() {
            return this.rtpData?.rtp_phase ? parseInt(this.rtpData.rtp_phase) : null;
        },
        regressPhaseOptions() {
            // Safely generate array of phases to regress to (1 to currentPhase-1)
            const phase = this.currentPhase;
            if (!phase || phase <= 1) return [];
            return Array.from({ length: phase - 1 }, (_, i) => i + 1);
        },
        daysInCurrentPhase() {
            if (!this.phaseHistory || this.phaseHistory.length === 0) return 0;
            const lastPhase = this.phaseHistory[this.phaseHistory.length - 1];
            if (!lastPhase.entered_date) return 0;

            const entered = new Date(lastPhase.entered_date);
            const now = new Date();
            return Math.floor((now - entered) / (1000 * 60 * 60 * 24));
        },
        currentPhaseLead() {
            if (!this.phaseHistory || this.phaseHistory.length === 0) return null;
            const lastPhase = this.phaseHistory[this.phaseHistory.length - 1];
            return lastPhase.phase_lead_name;
        },
        maturityOutdated() {
            return this.rtpData?.maturity_outdated === true;
        },
        maturityDaysOld() {
            return this.rtpData?.maturity_days_old || 0;
        },
        isReviewOverdue() {
            if (!this.rtpData?.rtp_next_review_date) return false;
            const reviewDate = new Date(this.rtpData.rtp_next_review_date);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            return reviewDate < today;
        },
        canProgress() {
            if (!this.currentPhase || this.currentPhase >= 5) return false;
            if (this.hoursRemaining > 0) return false;

            const userRole = this.userData.position || this.userData.userType;
            return canProgressPhase(this.currentPhase, this.currentPhase + 1, userRole);
        },
        canComplete() {
            if (this.currentPhase !== 5) return false;
            const userRole = this.userData.position || this.userData.userType;
            return ['Head Sports Science', 'Admin'].includes(userRole);
        },
        hoursRemaining() {
            if (!this.phaseHistory || this.phaseHistory.length === 0) return 0;

            const lastPhase = this.phaseHistory[this.phaseHistory.length - 1];
            if (!lastPhase.entered_date) return 0;

            const minHours = getMinHoursBetweenPhases(this.rtpData?.maturity_at_injury);
            const entered = new Date(lastPhase.entered_date);
            const now = new Date();
            const hoursDiff = (now - entered) / (1000 * 60 * 60);

            return Math.max(0, Math.ceil(minHours - hoursDiff));
        },
        recommendedStaff() {
            return this.availableStaff.filter(s => s.isRecommended);
        },
        otherStaff() {
            return this.availableStaff.filter(s => !s.isRecommended);
        },
        allChecklistComplete() {
            return this.completeForm.checklist_physical &&
                this.completeForm.checklist_training &&
                this.completeForm.checklist_confidence;
        },
        canOverrideDuration() {
            const userRole = this.userData.position || this.userData.userType;
            return ['Admin', 'Physio', 'Head Physio'].includes(userRole);
        },
        canManageRecoveryDrills() {
            const allowedRoles = ['Admin', 'Physio', 'Head Physio', 'Sports Science', 'Head Sports Science'];
            const userRole = this.userData?.position || this.userData?.userType;
            return allowedRoles.includes(userRole);
        },
        filteredRecoveryLibraryDrills() {
            if (!this.recoveryDrillSearch) return this.recoveryLibraryDrills;
            const search = this.recoveryDrillSearch.toLowerCase();
            return this.recoveryLibraryDrills.filter(d =>
                (d.name || '').toLowerCase().includes(search) ||
                (d.category || '').toLowerCase().includes(search)
            );
        }
    },
    mounted() {
        this.loadRTPData();
    },
    watch: {
        injuryId: {
            handler() {
                this.loadRTPData();
            }
        }
    },
    methods: {
        getPhaseInfo(phase) {
            return RTP_PHASES[phase] || { name: 'Unknown', color: '#6c757d', bgColor: '#f8f9fa', description: '' };
        },

        getRequiredRolesText() {
            if (!this.currentPhase || this.currentPhase >= 5) return '';
            const roles = getRequiredRoles(this.currentPhase, this.currentPhase + 1);
            return roles.join(', ');
        },

        hasRegression(phase) {
            return this.phaseHistory.some(log => log.phase_number === phase && log.is_regression);
        },

        formatDate(dateStr) {
            if (!dateStr) return '-';
            const date = new Date(dateStr);
            return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
        },

        async loadRTPData() {
            this.loading = true;
            try {
                const response = await ReturnToPlayService.getRTPByInjuryId(this.injuryId);
                if (response.data.success) {
                    this.rtpData = response.data.injury;
                    this.phaseHistory = response.data.phase_history || [];

                    // Pre-fill edit form
                    this.editForm.restrictions = this.rtpData.rtp_restrictions || '';
                    this.editForm.next_review_date = this.rtpData.rtp_next_review_date || '';
                    this.editForm.target_date = this.rtpData.rtp_target_date || '';

                    // Set default regress phase
                    if (this.currentPhase > 1) {
                        this.regressForm.new_phase = this.currentPhase - 1;
                    }

                    // Load assigned recovery drills for current phase
                    await this.loadAssignedRecoveryDrills();
                }
            } catch (error) {
                console.error('Error loading RTP data:', error);
                this.$emit('error', 'Failed to load RTP data');
            } finally {
                this.loading = false;
            }
        },

        openProgressModal() {
            this.progressForm = {
                sign_off_comments: '',
                restrictions: '',
                next_review_date: this.getDefaultReviewDate(),
                phase_lead_id: ''
            };
            this.loadAvailableStaff();
            this.showProgressModal = true;
        },

        async loadAvailableStaff() {
            try {
                const response = await Users.find({
                    action: 'getStaffByClubId',
                    clubId: this.userData.clubId
                });

                if (response.data) {
                    // Get the lead roles for the NEXT phase
                    const nextPhase = this.currentPhase + 1;
                    const nextPhaseInfo = RTP_PHASES[nextPhase];
                    const allowedRoles = nextPhaseInfo?.leadRoles || [];

                    // Staff roles that should appear in the phase lead dropdown
                    // Include variations to handle inconsistent database values
                    const staffRoles = [
                        'Admin',
                        'Physio',
                        'Head Physio',
                        'HeadPhysio',
                        'Sports Science',
                        'Head Sports Science',
                        'HeadSportsScience',
                        'Coach',
                        'HeadCoach',
                        'Head Coach',
                        'Assistant Coach',
                        'Goalkeeper Coach',
                        'Goalkeeper coach',
                        'Goal keeper Coach',
                        'Medical',
                        'Doctor'
                    ];

                    // Helper function for case-insensitive role matching
                    const normalizeRole = (role) => (role || '').toLowerCase().trim();
                    const staffRolesLower = staffRoles.map(r => r.toLowerCase());

                    this.availableStaff = response.data.filter(user => {
                        const userPosition = normalizeRole(user.position);
                        const userType = normalizeRole(user.userType);
                        return staffRolesLower.includes(userPosition) || staffRolesLower.includes(userType);
                    }).map(user => {
                        const displayPosition = user.position || user.userType;
                        const userPositionLower = normalizeRole(user.position);
                        const userTypeLower = normalizeRole(user.userType);

                        // Check if this user's role is in the recommended roles for next phase
                        const isRecommended = allowedRoles.some(role => {
                            const roleLower = role.toLowerCase();
                            return userPositionLower === roleLower || userTypeLower === roleLower;
                        });

                        return {
                            id: user.id,
                            name: user.name,
                            position: displayPosition,
                            isRecommended: isRecommended
                        };
                    }).sort((a, b) => {
                        // Sort recommended leads first
                        if (a.isRecommended && !b.isRecommended) return -1;
                        if (!a.isRecommended && b.isRecommended) return 1;
                        return a.name.localeCompare(b.name);
                    });

                    console.log('Available staff loaded:', this.availableStaff.length, 'members');
                }
            } catch (error) {
                console.error('Error loading staff:', error);
                this.availableStaff = [];
            }
        },

        openRegressModal() {
            this.regressForm = {
                adverse_reaction: '',
                new_phase: this.currentPhase - 1,
                restrictions: '',
                sign_off_comments: ''
            };
            this.showRegressModal = true;
        },

        openCompleteModal() {
            this.completeForm = {
                sign_off_comments: '',
                checklist_physical: false,
                checklist_training: false,
                checklist_confidence: false
            };
            this.showCompleteModal = true;
        },

        openEditModal() {
            this.editForm = {
                restrictions: this.rtpData?.rtp_restrictions || '',
                next_review_date: this.rtpData?.rtp_next_review_date || '',
                target_date: this.rtpData?.rtp_target_date || '',
                override_duration: false,
                override_reason: ''
            };
            this.showEditModal = true;
        },

        getDefaultReviewDate() {
            const date = new Date();
            date.setDate(date.getDate() + 7);
            return date.toISOString().split('T')[0];
        },

        async submitProgress() {
            this.isSubmitting = true;
            try {
                const response = await ReturnToPlayService.progressRTPPhase({
                    injury_id: this.injuryId,
                    user_id: this.userData.id,
                    user_name: this.userData.name,
                    user_role: this.userData.position || this.userData.userType,
                    sign_off_comments: this.progressForm.sign_off_comments,
                    restrictions: this.progressForm.restrictions,
                    next_review_date: this.progressForm.next_review_date,
                    phase_lead_id: this.progressForm.phase_lead_id || null
                });

                if (response.data.success) {
                    this.$emit('phase-updated', response.data);
                    this.showProgressModal = false;
                    await this.loadRTPData();
                } else {
                    alert(response.data.message || 'Failed to progress phase');
                }
            } catch (error) {
                console.error('Error progressing phase:', error);
                alert('An error occurred. Please try again.');
            } finally {
                this.isSubmitting = false;
            }
        },

        async submitRegress() {
            if (!this.regressForm.adverse_reaction.trim()) {
                alert('Adverse reaction description is required');
                return;
            }

            this.isSubmitting = true;
            try {
                const response = await ReturnToPlayService.regressRTPPhase({
                    injury_id: this.injuryId,
                    user_id: this.userData.id,
                    user_name: this.userData.name,
                    user_role: this.userData.position || this.userData.userType,
                    adverse_reaction: this.regressForm.adverse_reaction,
                    new_phase: this.regressForm.new_phase,
                    restrictions: this.regressForm.restrictions,
                    sign_off_comments: this.regressForm.sign_off_comments
                });

                if (response.data.success) {
                    this.$emit('phase-updated', response.data);
                    this.showRegressModal = false;
                    await this.loadRTPData();
                } else {
                    alert(response.data.message || 'Failed to regress phase');
                }
            } catch (error) {
                console.error('Error regressing phase:', error);
                alert('An error occurred. Please try again.');
            } finally {
                this.isSubmitting = false;
            }
        },

        async submitComplete() {
            if (!this.completeForm.sign_off_comments.trim()) {
                alert('Final clearance comments are required');
                return;
            }

            this.isSubmitting = true;
            try {
                const response = await ReturnToPlayService.completeRTP({
                    injury_id: this.injuryId,
                    user_id: this.userData.id,
                    user_name: this.userData.name,
                    user_role: this.userData.position || this.userData.userType,
                    sign_off_comments: this.completeForm.sign_off_comments
                });

                if (response.data.success) {
                    this.$emit('rtp-completed', response.data);
                    this.showCompleteModal = false;
                    await this.loadRTPData();
                } else {
                    alert(response.data.message || 'Failed to complete RTP');
                }
            } catch (error) {
                console.error('Error completing RTP:', error);
                alert('An error occurred. Please try again.');
            } finally {
                this.isSubmitting = false;
            }
        },

        async submitEditDetails() {
            this.isSubmitting = true;
            try {
                const payload = {
                    injury_id: this.injuryId,
                    restrictions: this.editForm.restrictions,
                    next_review_date: this.editForm.next_review_date,
                    target_date: this.editForm.target_date
                };

                // If overriding duration, include override data
                if (this.editForm.override_duration && this.canOverrideDuration) {
                    payload.override_duration = true;
                    payload.override_reason = this.editForm.override_reason;
                    payload.override_by_id = this.userData.id;
                    payload.override_by_name = this.userData.name;
                    payload.override_by_role = this.userData.position || this.userData.userType;
                }

                const response = await ReturnToPlayService.updateRTPDetails(payload);

                if (response.data.success) {
                    this.$emit('details-updated', response.data);
                    this.showEditModal = false;
                    await this.loadRTPData();

                    if (this.editForm.override_duration) {
                        this.$emit('duration-override-applied');
                    }
                } else {
                    alert(response.data.message || 'Failed to update details');
                }
            } catch (error) {
                console.error('Error updating details:', error);
                alert('An error occurred. Please try again.');
            } finally {
                this.isSubmitting = false;
            }
        },

        // ── Recovery Drills ─────────────────────────────────────────────────────

        async loadAssignedRecoveryDrills() {
            if (!this.currentPhase) {
                this.assignedRecoveryDrills = [];
                return;
            }
            try {
                const response = await ReturnToPlayService.getPlayerRecoveryDrillAssignments(
                    this.injuryId,
                    this.currentPhase
                );
                this.assignedRecoveryDrills = Array.isArray(response.data) ? response.data : [];
            } catch (error) {
                console.error('Error loading recovery drill assignments:', error);
                this.assignedRecoveryDrills = [];
            }
        },

        async openRecoveryDrillPicker() {
            this.showRecoveryDrillPicker = true;
            this.recoveryDrillSearch = '';
            if (this.recoveryLibraryDrills.length === 0) {
                await this.loadRecoveryLibrary();
            }
        },

        async loadRecoveryLibrary() {
            this.loadingRecoveryDrills = true;
            try {
                const response = await ReturnToPlayService.getRecoveryDrillsForClub(
                    this.userData.clubId
                );
                this.recoveryLibraryDrills = Array.isArray(response.data) ? response.data : [];
            } catch (error) {
                console.error('Error loading recovery library:', error);
                this.recoveryLibraryDrills = [];
            } finally {
                this.loadingRecoveryDrills = false;
            }
        },

        isRecoveryDrillAssigned(drillId) {
            return this.assignedRecoveryDrills.some(a => String(a.training_plan_id) === String(drillId));
        },

        async toggleRecoveryDrill(drill) {
            if (this.isRecoveryDrillAssigned(drill.id)) {
                const assignment = this.assignedRecoveryDrills.find(
                    a => String(a.training_plan_id) === String(drill.id)
                );
                if (assignment) {
                    await this.removeRecoveryDrill(assignment.id);
                }
            } else {
                await this.assignRecoveryDrill(drill);
            }
        },

        async assignRecoveryDrill(drill) {
            try {
                await ReturnToPlayService.assignRecoveryDrillToPlayer({
                    injury_id: this.injuryId,
                    player_id: this.rtpData.player_id,
                    training_plan_id: drill.id,
                    phase_number: this.currentPhase,
                    assigned_by: this.userData.id
                });
                await this.loadAssignedRecoveryDrills();
            } catch (error) {
                console.error('Error assigning recovery drill:', error);
            }
        },

        async removeRecoveryDrill(assignmentId) {
            try {
                await ReturnToPlayService.removeRecoveryDrillAssignment(assignmentId);
                await this.loadAssignedRecoveryDrills();
            } catch (error) {
                console.error('Error removing recovery drill:', error);
            }
        }
    }
};
</script>

<style scoped>
.rtp-phase-tracker {
    background: white;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.tracker-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    flex-wrap: wrap;
    gap: 10px;
}

.tracker-header h4 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 8px;
}

.tracker-header h4 i {
    color: #007bff;
}

.maturity-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 20px;
    font-size: 13px;
    font-weight: 500;
}

.maturity-prephv {
    background: #e0f7fa;
    color: #006064;
}

.maturity-circaphv {
    background: #fff3e0;
    color: #e65100;
}

.maturity-postphv {
    background: #e8f5e9;
    color: #1b5e20;
}

.maturity-warning {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-left: 8px;
    padding: 2px 8px;
    background: #ffeb3b;
    border-radius: 10px;
    font-size: 11px;
    color: #5d4037;
}

.maturity-warning-banner {
    background: #fff3cd;
    border: 1px solid #ffc107;
    border-radius: 8px;
    padding: 12px 16px;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    gap: 10px;
    color: #856404;
}

.maturity-warning-banner i {
    font-size: 20px;
    color: #ff9800;
}

/* Phase Stepper */
.phase-stepper {
    display: flex;
    justify-content: space-between;
    margin-bottom: 24px;
    padding: 0 10px;
}

.phase-step {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 1;
    position: relative;
}

.step-indicator {
    display: flex;
    align-items: center;
    width: 100%;
    position: relative;
}

.step-circle {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: #e9ecef;
    border: 3px solid #dee2e6;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 14px;
    color: #6c757d;
    z-index: 1;
    transition: all 0.3s ease;
    flex-shrink: 0;
    margin: 0 auto;
}

.phase-step.completed .step-circle {
    background: #28a745;
    border-color: #28a745;
    color: white;
}

.phase-step.active .step-circle {
    background: #007bff;
    border-color: #007bff;
    color: white;
    transform: scale(1.1);
    box-shadow: 0 0 0 4px rgba(0, 123, 255, 0.2);
}

.phase-step.regression .step-circle {
    background: #ffc107;
    border-color: #ffc107;
    color: #212529;
}

.step-line {
    position: absolute;
    top: 50%;
    left: calc(50% + 20px);
    width: calc(100% - 40px);
    height: 3px;
    background: #dee2e6;
    transform: translateY(-50%);
}

.step-line.completed {
    background: #28a745;
}

.step-content {
    margin-top: 8px;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.step-name {
    display: block;
    font-size: 11px;
    color: #6c757d;
    max-width: 80px;
}

.step-short {
    display: none;
    font-size: 12px;
    font-weight: 600;
    color: #495057;
}

/* Current Phase Details */
.phase-info-card {
    border: 2px solid #dee2e6;
    border-radius: 12px;
    overflow: hidden;
    margin-bottom: 20px;
}

.phase-header {
    padding: 16px 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.phase-title {
    display: flex;
    align-items: center;
    gap: 12px;
}

.phase-badge {
    padding: 4px 12px;
    border-radius: 20px;
    color: white;
    font-size: 12px;
    font-weight: 600;
}

.phase-title h5 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
}

.days-counter {
    display: flex;
    flex-direction: column;
    align-items: center;
    background: white;
    padding: 8px 16px;
    border-radius: 8px;
}

.days-number {
    font-size: 24px;
    font-weight: 700;
    line-height: 1;
}

.days-label {
    font-size: 11px;
    color: #6c757d;
    text-transform: uppercase;
}

.phase-body {
    padding: 20px;
}

.phase-description {
    color: #495057;
    margin-bottom: 16px;
}

.phase-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    margin-bottom: 16px;
}

.meta-item {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
}

.meta-item i {
    color: #6c757d;
}

.meta-label {
    color: #6c757d;
}

.meta-value {
    font-weight: 500;
}

.meta-value.overdue {
    color: #dc3545;
}

.overdue-badge {
    display: inline-block;
    background: #dc3545;
    color: white;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 10px;
    margin-left: 6px;
}

.restrictions-section {
    background: #fff3cd;
    border-radius: 8px;
    padding: 12px 16px;
}

.restrictions-section h6 {
    margin: 0 0 8px 0;
    font-size: 14px;
    display: flex;
    align-items: center;
    gap: 6px;
    color: #856404;
}

.restrictions-section p {
    margin: 0;
    color: #856404;
}

/* Phase Overview Section */
.phase-overview-section {
    background: #f8f9fa;
    border-radius: 8px;
    padding: 16px;
    margin-top: 16px;
    border: 1px solid #e9ecef;
}

.phase-overview-section h6 {
    margin: 0 0 12px 0;
    font-size: 14px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 6px;
    color: #495057;
}

.overview-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    margin-bottom: 16px;
}

.overview-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.overview-item.full-width {
    grid-column: 1 / -1;
}

.overview-label {
    font-size: 12px;
    color: #6c757d;
    font-weight: 500;
}

.overview-value {
    font-size: 14px;
    color: #212529;
}

.progression-criteria {
    background: white;
    border-radius: 6px;
    padding: 12px;
    border: 1px solid #dee2e6;
}

.criteria-label {
    font-size: 12px;
    color: #6c757d;
    font-weight: 600;
    display: block;
    margin-bottom: 8px;
}

.criteria-list {
    margin: 0;
    padding-left: 1.5rem;
    list-style-type: none;
}

.criteria-list li {
    position: relative;
    padding: 4px 0;
    font-size: 13px;
    color: #495057;
}

.criteria-list li::before {
    content: '✓';
    position: absolute;
    left: -1rem;
    color: #28a745;
    font-weight: bold;
}

/* Action Buttons */
.phase-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    align-items: center;
    padding-top: 16px;
    border-top: 1px solid #e9ecef;
}

.btn-progress {
    display: flex;
    align-items: center;
    gap: 6px;
}

.btn-regress {
    display: flex;
    align-items: center;
    gap: 6px;
}

.permission-warning,
.time-warning {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: #6c757d;
    padding: 8px 12px;
    background: #f8f9fa;
    border-radius: 6px;
    width: 100%;
    margin-top: 8px;
}

.permission-warning i,
.time-warning i {
    color: #ffc107;
}

/* Modal Styles */
.modal-phase-info {
    margin-bottom: 20px;
}

.phase-transition {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 16px;
    background: #f8f9fa;
    border-radius: 8px;
}

.from-phase,
.to-phase {
    padding: 8px 16px;
    border-radius: 20px;
    color: white;
    font-weight: 500;
    font-size: 14px;
}

.phase-transition i {
    font-size: 20px;
    color: #6c757d;
}

.modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 20px;
    padding-top: 16px;
    border-top: 1px solid #e9ecef;
}

.form-label.required::after {
    content: '';
    color: #dc3545;
    margin-left: 4px;
}

.clearance-checklist {
    background: #f8f9fa;
    border-radius: 8px;
    padding: 16px;
    margin-top: 16px;
}

.clearance-checklist h6 {
    margin: 0 0 12px 0;
    font-size: 14px;
    font-weight: 600;
}

.checklist-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 0;
}

.checklist-item input[type="checkbox"] {
    width: 18px;
    height: 18px;
}

.checklist-item label {
    margin: 0;
    cursor: pointer;
}

/* Duration Override Section */
.duration-override-section {
    background: #fff8e1;
    border: 1px solid #ffc107;
    border-radius: 8px;
    padding: 12px;
    margin-top: 8px;
}

.override-warning {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-size: 0.9rem;
    margin-bottom: 12px;
}

.override-warning i {
    margin-top: 2px;
    color: #856404;
}

.duration-override-section .form-check {
    background: white;
    padding: 12px;
    border-radius: 6px;
    border: 1px solid #e0e0e0;
}

.duration-override-section .form-check-label small {
    color: #666;
    font-size: 0.8rem;
}

.override-confirmation {
    background: #fff3cd;
    padding: 12px;
    border-radius: 6px;
    border: 1px solid #ffeeba;
}

.override-confirmation .form-label.required::after {
    content: '';
    color: #dc3545;
}

/* Responsive */
@media (max-width: 768px) {
    .phase-stepper {
        flex-wrap: wrap;
        gap: 16px;
    }

    .phase-step {
        flex: 0 0 calc(33.333% - 12px);
    }

    .step-line {
        display: none;
    }

    .step-name {
        display: none;
    }

    .step-short {
        display: block;
    }

    .phase-header {
        flex-direction: column;
        gap: 12px;
        align-items: flex-start;
    }

    .days-counter {
        flex-direction: row;
        gap: 6px;
    }

    .phase-meta {
        flex-direction: column;
        gap: 8px;
    }

    .phase-actions {
        flex-direction: column;
    }

    .phase-actions .btn {
        width: 100%;
    }
}

@media (max-width: 480px) {
    .phase-step {
        flex: 0 0 calc(50% - 8px);
    }

    .step-circle {
        width: 36px;
        height: 36px;
        font-size: 12px;
    }

    .tracker-header {
        flex-direction: column;
        align-items: flex-start;
    }
}

/* ── Recovery Drills Section ─────────────────────────────────────────────── */

.recovery-drills-section {
    margin-top: 20px;
    padding-top: 20px;
    border-top: 1px solid #e9ecef;
}

.recovery-drills-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
}

.recovery-drills-header h6 {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: #495057;
    display: flex;
    align-items: center;
    gap: 6px;
}

.recovery-drills-header h6 i {
    color: #20c997;
}

.recovery-empty-state {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #adb5bd;
    font-size: 13px;
    padding: 12px 0;
}

.recovery-empty-state i {
    font-size: 18px;
}

.recovery-drill-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.recovery-drill-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    background: #f8f9fa;
    border-radius: 8px;
    border: 1px solid #e9ecef;
}

.drill-item-image {
    width: 40px;
    height: 40px;
    border-radius: 6px;
    overflow: hidden;
    flex-shrink: 0;
}

.drill-item-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.drill-item-image--placeholder {
    background: #dee2e6;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #adb5bd;
    font-size: 18px;
}

.drill-item-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.drill-item-name {
    font-size: 13px;
    font-weight: 500;
    color: #212529;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.drill-item-category {
    font-size: 11px;
    color: #6c757d;
    background: #e9ecef;
    padding: 1px 6px;
    border-radius: 10px;
    align-self: flex-start;
}

.drill-remove-btn {
    flex-shrink: 0;
    padding: 2px 6px;
    line-height: 1;
}

/* ── Recovery Drill Picker Modal ─────────────────────────────────────────── */

.recovery-picker-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 1050;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
}

.recovery-picker-modal {
    background: white;
    border-radius: 12px;
    width: 100%;
    max-width: 680px;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}

.recovery-picker-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    border-bottom: 1px solid #e9ecef;
}

.recovery-picker-header h5 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 8px;
    color: #212529;
}

.recovery-picker-header h5 i {
    color: #20c997;
}

.recovery-picker-search {
    padding: 12px 20px;
    border-bottom: 1px solid #e9ecef;
}

.recovery-picker-loading,
.recovery-picker-empty {
    padding: 40px 20px;
    text-align: center;
    color: #6c757d;
    font-size: 14px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
}

.recovery-picker-empty i {
    font-size: 32px;
    color: #dee2e6;
}

.recovery-picker-grid {
    overflow-y: auto;
    padding: 16px 20px;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.recovery-picker-card {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    border: 2px solid #e9ecef;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.15s ease;
    background: white;
}

.recovery-picker-card:hover {
    border-color: #007bff;
    background: #f0f7ff;
}

.recovery-picker-card.is-assigned {
    border-color: #28a745;
    background: #f0fff4;
}

.picker-card-image {
    width: 48px;
    height: 48px;
    border-radius: 8px;
    overflow: hidden;
    flex-shrink: 0;
}

.picker-card-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.picker-card-image--placeholder {
    background: #dee2e6;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #adb5bd;
    font-size: 20px;
}

.picker-card-body {
    flex: 1;
    min-width: 0;
}

.picker-card-name {
    font-size: 14px;
    font-weight: 500;
    color: #212529;
}

.picker-card-category {
    display: inline-block;
    font-size: 11px;
    color: #6c757d;
    background: #e9ecef;
    padding: 2px 8px;
    border-radius: 10px;
    margin-top: 2px;
}

.picker-card-intro {
    font-size: 12px;
    color: #6c757d;
    margin-top: 4px;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
}

.picker-card-action {
    flex-shrink: 0;
    font-size: 22px;
}

.assigned-icon {
    color: #28a745;
}

.add-icon {
    color: #007bff;
}
</style>
